/**
 * describeNativeValidity — which catalogue message replaces the browser's own validation bubble.
 *
 * The browser writes that bubble in ITS language («Please fill out this field.»), not the app's: in a
 * Spanish interface every form without `noValidate` and zod messages showed English text (about 58 of
 * them, measured 2026-09-29). The forms that validate with zod already show catalogue text; this covers
 * the rest with ONE mechanism (see `NativeValidationTranslator`) instead of a per-form patch.
 *
 * Pure and structural: takes the control's `validity` and attributes, returns `{ key, values }` for the
 * `validation` namespace (`native.*`), or `null` when there is nothing to say (valid, or the app already
 * set its own custom message).
 */

export interface NativeValidationMessage {
  /** Key inside the `validation` namespace. */
  key: string
  /** Interpolation values (`count`, `min`, `max`, `step`, `title`). */
  values: Record<string, unknown>
}

/** The parts of a form control this reads: satisfied by input / select / textarea. */
export interface ValidatableControl {
  validity: Pick<
    ValidityState,
    | 'valid'
    | 'customError'
    | 'valueMissing'
    | 'typeMismatch'
    | 'patternMismatch'
    | 'tooShort'
    | 'tooLong'
    | 'rangeUnderflow'
    | 'rangeOverflow'
    | 'stepMismatch'
    | 'badInput'
  >
  tagName: string
  type?: string
  title?: string
  min?: string
  max?: string
  step?: string
  minLength?: number
  maxLength?: number
}

const valueMissingKind = (control: ValidatableControl): string => {
  if (control.tagName === 'SELECT') return 'select'
  switch (control.type) {
    case 'checkbox':
      return 'checkbox'
    case 'radio':
      return 'radio'
    case 'file':
      return 'file'
    default:
      return 'default'
  }
}

export function describeNativeValidity(
  control: ValidatableControl,
): NativeValidationMessage | null {
  const { validity } = control
  if (validity.valid || validity.customError) return null

  if (validity.valueMissing) {
    return { key: `native.valueMissing.${valueMissingKind(control)}`, values: {} }
  }
  if (validity.typeMismatch) {
    const kind = control.type === 'email' || control.type === 'url' ? control.type : 'default'
    return { key: `native.typeMismatch.${kind}`, values: {} }
  }
  if (validity.patternMismatch) {
    const title = control.title?.trim()
    return title
      ? { key: 'native.patternMismatch.withTitle', values: { title } }
      : { key: 'native.patternMismatch.default', values: {} }
  }
  if (validity.tooShort) return { key: 'native.tooShort', values: { count: control.minLength } }
  if (validity.tooLong) return { key: 'native.tooLong', values: { count: control.maxLength } }
  if (validity.rangeUnderflow) return { key: 'native.rangeUnderflow', values: { min: control.min } }
  if (validity.rangeOverflow) return { key: 'native.rangeOverflow', values: { max: control.max } }
  if (validity.stepMismatch) return { key: 'native.stepMismatch', values: { step: control.step } }
  if (validity.badInput) return { key: 'native.badInput', values: {} }
  return null
}
